/**
 * Search Term V2 — columns, measures, group-by, and ComplexFilter configs.
 */

import { commonFilterOperators } from "../../../components/Filter/FilterData/quadrantFilter";

export const SEARCH_TERM_V2_DEFAULT_DIMENSIONS = [
  "search_term",
  "campaign_name",
  "campaign_id",
  "ad_group_id",
  "match_type",
  "type",
  "targeting",
];

/** Fetched as measures (not group-by dims). */
export const SEARCH_TERM_V2_ALWAYS_MEASURES = [
  "ad_group_name",
  "portfolio_name",
  "campaign_type",
  "targeting_type",
  // "campaign_format",
];

export const SEARCH_TERM_V2_PAGE_MEASURES = [
  "spends",
  "impressions",
  "clicks",
  "orders",
  "revenue",
  "roas",
  "ctr",
  "acos",
];

export const SEARCH_TERM_V2_GROUP_BY_OPTIONS = [
  {
    label: "Search Term",
    value: "search_term",
    dimensions: SEARCH_TERM_V2_DEFAULT_DIMENSIONS,
  },
];

/** Visible table columns (sticky + metrics). campaign_name / ad_group_name come via innerKeys. */
export const SEARCH_TERM_V2_COLUMN_CONFIG = [
  {
    key: "search_term",
    title: "Search Term",
    shortTitle: "Search Term",
    type: "string",
    dataIndex: "search_term",
    isDimension: true,
    isSticky: true,
    minWidth: 200,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "campaign_id",
    innerKeys: {
      productName: "campaign_name",
      asin: { label: "Campaign ID", value: "campaign_id" },
    },
    title: "Campaign",
    shortTitle: "Campaign Name",
    type: "string",
    dataIndex: "campaign_id",
    isDimension: true,
    isSticky: true,
    minWidth: 240,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "ad_group_id",
    innerKeys: {
      productName: "ad_group_name",
      asin: { label: "Ad Group ID", value: "ad_group_id" },
    },
    title: "Ad Group",
    shortTitle: "Ad Group",
    type: "string",
    dataIndex: "ad_group_id",
    isDimension: true,
    isSticky: false,
    minWidth: 200,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "portfolio_name",
    title: "Portfolio",
    shortTitle: "Portfolio",
    type: "string",
    dataIndex: "portfolio_name",
    minWidth: 140,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "targeting",
    title: "Targeting",
    shortTitle: "Targeting",
    type: "string",
    dataIndex: "targeting",
    isDimension: true,
    minWidth: 160,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "targeting_type",
    title: "Targeting Type",
    shortTitle: "Targeting Type",
    type: "string",
    dataIndex: "targeting_type",
    minWidth: 140,
    alignment: "left",
    isSingleColumn: true,
    isAttribute: true,
  },
  {
    key: "match_type",
    title: "Match Type",
    shortTitle: "Match Type",
    type: "string",
    dataIndex: "match_type",
    isDimension: true,
    minWidth: 120,
    alignment: "left",
    isSingleColumn: true,
  },
  {
    key: "spends",
    title: "Spends",
    shortTitle: "Spends",
    type: "number",
    dataIndex: "spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
    minWidth: 110,
    alignment: "right",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "impressions",
    title: "Impressions",
    shortTitle: "Impressions",
    type: "number",
    dataIndex: "impressions",
    maximumFractionDigits: 0,
    minWidth: 120,
    alignment: "right",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "clicks",
    title: "Clicks",
    shortTitle: "Clicks",
    type: "number",
    dataIndex: "clicks",
    maximumFractionDigits: 0,
    minWidth: 100,
    alignment: "right",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "orders",
    title: "Orders",
    shortTitle: "Orders",
    type: "number",
    dataIndex: "orders",
    maximumFractionDigits: 0,
    minWidth: 100,
    alignment: "right",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "revenue",
    title: "Revenue",
    shortTitle: "Revenue",
    type: "number",
    dataIndex: "revenue",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
    minWidth: 110,
    alignment: "right",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "roas",
    title: "ROAS",
    shortTitle: "ROAS",
    type: "number",
    dataIndex: "roas",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "ctr",
    title: "CTR",
    shortTitle: "CTR",
    type: "number",
    dataIndex: "ctr",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    format: "percent",
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  {
    key: "acos",
    title: "ACOS",
    shortTitle: "ACOS",
    type: "number",
    dataIndex: "acos",
    format: "percent",
    maximumFractionDigits: 2,
    minWidth: 90,
    alignment: "right",
    viewType: ["PERFORMANCE"],
  },
];

export function getSearchTermV2Columns() {
  let cumulativeStickyLeft = 0;
  return SEARCH_TERM_V2_COLUMN_CONFIG.map((column, index) => {
    const minWidth = column.minWidth || 120;
    const updated = {
      ...column,
      stickyLeft: index === 0 ? 0 : cumulativeStickyLeft,
    };
    if (column.isSticky) {
      cumulativeStickyLeft += minWidth;
    }
    return updated;
  });
}

export const FiltersDimensionDataSearchTermV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [
      {
        name: "Search Term",
        id: "search_term",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Search Term",
      },
      {
        name: "Campaign Name",
        id: "campaign_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "campaign_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "campaign_names_api_key",
        backendSearch: true,
        grouping: "Campaign Name",
      },
      {
        name: "Portfolio Name",
        id: "portfolio_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "portfolio_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "portfolio_names_api_key",
        backendSearch: true,
        grouping: "Portfolio Name",
      },
      {
        name: "Ad Group Name",
        id: "ad_group_name",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: true,
        optionsKey: "ad_group_names_api_key",
        source: "BackendAll",
        optionsType: "Static",
        dataFromApi: true,
        apiKey: "ad_group_names_api_key",
        backendSearch: true,
        grouping: "Ad Group Name",
      },
      {
        name: "Targeting",
        id: "targeting",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Targeting",
      },
      {
        name: "Targeting Type",
        id: "targeting_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey:
          "G.Keyword,C.Keyword,NB.Keyword,Audience,Automatic,NB.Product,B.Keyword,B.Audience,B.Product",
        grouping: "Targeting Type",
      },
      {
        name: "Match Type",
        id: "match_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "EXACT,BROAD,PHRASE,Automatic,Product",
        grouping: "Match Type",
      },
      {
        name: "Campaign Type",
        id: "campaign_type",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "SP,SB(V),SB",
        grouping: "Campaign Type",
      },
      {
        name: "Campaign Format",
        id: "campaign_format",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "Prospect,Retarget",
        grouping: "Campaign Format",
      },
      {
        name: "Campaign Status",
        id: "campaign_status",
        datatype: "string",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        optionsType: "Static",
        optionsKey: "PAUSED,ENABLED,ARCHIVED",
        grouping: "Campaign Status",
      },
    ],
  },
};

export const FiltersMeasuresDataSearchTermV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [
      {
        name: "Spends",
        id: "spends",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Spends",
      },
      {
        name: "Impressions",
        id: "impressions",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Impressions",
      },
      {
        name: "Clicks",
        id: "clicks",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Clicks",
      },
      {
        name: "Orders",
        id: "orders",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Orders",
      },
      {
        name: "Revenue",
        id: "revenue",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "Revenue",
      },
      {
        name: "ROAS",
        id: "roas",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "ROAS",
      },
      {
        name: "CTR",
        id: "ctr",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "CTR",
      },
      {
        name: "ACOS",
        id: "acos",
        datatype: "int",
        type: "default",
        placeholder: "Enter value",
        dynamicOptions: false,
        autoComplete: true,
        source: "Adyogi",
        grouping: "ACOS",
      },
    ],
  },
};

export const FiltersCampaignDataSearchTermV2 = {
  amazon: {
    operators: commonFilterOperators,
    fields: [],
  },
};
